// File: packages/calculator/components/Disclaimers.tsx

import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp, HeartHandshake } from 'lucide-react';

type Props = {
  socialImpactPct?: number;
  disclaimers?: string[];
  defaultOpen?: boolean;
};

/**
 * A collapsible section for displaying legal disclaimers and
 * other important, but secondary, information.
 */
export function Disclaimers({ socialImpactPct, disclaimers, defaultOpen = false }: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const hasContent = socialImpactPct || (disclaimers && disclaimers.length > 0);

  if (!hasContent) {
    return null;
  }

  return (
    <section>
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 border-2 border-black rounded-xl bg-slate-50"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5" />
          <span className="font-extrabold text-slate-900">Important Information</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div className="p-4 border-2 border-t-0 border-black rounded-b-xl bg-white space-y-3 text-sm text-slate-700">
          {typeof socialImpactPct === 'number' && (
            <div className="flex items-start gap-2">
              <HeartHandshake className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
              <span>
                {socialImpactPct}% of platform revenue funds non-profit AI deployments. Impact is reported monthly.
              </span>
            </div>
          )}
          {disclaimers?.map((disclaimer, i) => (
            <div key={i} className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
              <span>{disclaimer}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}